/**
 * Password reset without leaving Settings (DOP-275).
 *
 * The two affordances that point at password reset used to be links to
 * `/forgot-password`, which threw away the settings page to ask for an
 * address the app already knows. The whole flow — the address step included —
 * happens here instead, and the field starts filled in with the signed-in
 * user's email.
 *
 * Editable anyway: an account whose sign-in address is a shared alias, or one
 * being reset on someone else's behalf, is the case the pre-fill would
 * otherwise make impossible. The server bounds the abuse (three sends per 15
 * minutes per address), not this field.
 *
 * Step two is the same confirmation the standalone page shows, minus the way
 * back to login — the user is already signed in, so there is nothing to go
 * back to.
 */

import React, { useRef, useState } from "react";
import ForgotPasswordSent from "../ForgotPasswordSent";
import ModalDialog from "../ModalDialog";
import { useForgotPasswordSend } from "../../hooks/useForgotPasswordSend";

export interface ForgotPasswordDialogProps {
	/** The signed-in user's address — the field's starting value. */
	initialEmail: string;
	onClose: () => void;
}

const TITLE_ID = "settings-forgot-password-title";
const EMAIL_FIELD_ID = "settings-forgot-password-email";

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
	initialEmail,
	onClose,
}) => {
	const [email, setEmail] = useState(initialEmail);
	const emailRef = useRef<HTMLInputElement | null>(null);
	const { sentTo, sending, error, cooldownSeconds, send, resend } =
		useForgotPasswordSend();

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		await send(email);
	};

	return (
		<ModalDialog
			titleId={TITLE_ID}
			// The email field on step one; the dialog itself on step two, where
			// the only control is the resend button and it starts disabled.
			initialFocusRef={emailRef}
			refocusOn={sentTo === null ? "form" : "sent"}
			onClose={onClose}
		>
			{sentTo === null ? (
				<form onSubmit={handleSubmit}>
					<h2
						id={TITLE_ID}
						className="text-lg font-semibold text-text-base"
					>
						Reset your password
					</h2>
					<p className="mt-2 text-base text-text-muted">
						We'll email you a link to set a new password. You don't need your
						current one.
					</p>

					{error && (
						<div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
							<p className="text-sm text-red-800">{error}</p>
						</div>
					)}

					<label
						htmlFor={EMAIL_FIELD_ID}
						className="mt-5 block text-sm font-medium text-gray-700"
					>
						Email Address
					</label>
					<input
						id={EMAIL_FIELD_ID}
						type="email"
						required
						ref={emailRef}
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						disabled={sending}
						autoComplete="email"
						className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-base text-text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#008782] disabled:bg-gray-50"
					/>

					<div className="mt-5 flex items-center justify-end gap-3">
						<button
							type="button"
							onClick={onClose}
							className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={sending || !email}
							className="rounded-lg bg-[#008782] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#004C4B] disabled:cursor-not-allowed disabled:opacity-50"
						>
							{sending ? "Sending..." : "Send reset link"}
						</button>
					</div>
				</form>
			) : (
				<ForgotPasswordSent
					email={sentTo}
					titleId={TITLE_ID}
					cooldownSeconds={cooldownSeconds}
					sending={sending}
					error={error}
					onResend={resend}
					footer={
						<button
							type="button"
							onClick={onClose}
							className="ml-auto font-medium text-primary hover:opacity-80"
						>
							Close
						</button>
					}
				/>
			)}
		</ModalDialog>
	);
};

export default ForgotPasswordDialog;
